import Modal from 'tailwind-components/Modal/Modal';
import Button from 'tailwind-components/Button/Button';
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toLocalISOString } from 'helpers/dashboard/calendar-events';
import { showAlert } from 'state/actions/site';
import { useDispatch } from 'react-redux';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';

const TITLE_MAX = 100;
const DESCRIPTION_MAX = 75;

function toDatetimeLocalValue(isoStr) {
  if (!isoStr || isoStr === '') return '';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day}T${h}:${min}`;
}

function fromDatetimeLocalToISO(value) {
  if (!value || value === '') return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return toLocalISOString(d);
}

export default function NewEventModal({
  mode,
  event: initialEvent,
  closeModal,
  submitEvent
}) {
  const dispatch = useDispatch();
  const isEdit = mode === 'edit';
  const original = useMemo(
    () =>
      initialEvent
        ? {
            title: initialEvent.title ?? '',
            description: initialEvent.description ?? null,
            startTime: initialEvent.startTime ?? ''
          }
        : null,
    [initialEvent]
  );

  const [title, setTitle] = useState(original?.title ?? '');
  const [description, setDescription] = useState(
    initialEvent?.description ?? ''
  );
  const [startTimeLocal, setStartTimeLocal] = useState(
    toDatetimeLocalValue(original?.startTime ?? '')
  );
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (original) {
      setTitle(original.title);
      setDescription(original.description ?? '');
      setStartTimeLocal(toDatetimeLocalValue(original.startTime));
    }
  }, [original]);

  const startTime = useMemo(
    () => fromDatetimeLocalToISO(startTimeLocal),
    [startTimeLocal]
  );

  const current = useMemo(
    () => ({
      title: title.trim(),
      description: description.trim() === '' ? null : description.trim(),
      startTime
    }),
    [title, description, startTime]
  );

  const hasChanges = useMemo(() => {
    if (!original) return true;
    return (
      current.title !== (original.title ?? '') ||
      (current.description ?? '') !== (original.description ?? '') ||
      current.startTime !== (original.startTime ?? '')
    );
  }, [original, current]);

  const titleError = title.length > TITLE_MAX;
  const descriptionError = description.length > DESCRIPTION_MAX;
  const canSaveAdd =
    title.trim() !== '' && !titleError && !descriptionError && startTime !== '';
  const canSaveEdit =
    hasChanges &&
    !titleError &&
    !descriptionError &&
    title.trim() !== '' &&
    startTime !== '';

  const handleSubmit = async action => {
    setError('');
    if (action === 'add' && !canSaveAdd) return;
    if (action === 'edit' && !canSaveEdit) return;
    setIsSubmitting(true);
    const payload = {
      action,
      title: title.trim(),
      description: description.trim() === '' ? null : description.trim(),
      startTime,
      endTime: startTime
    };
    if (action === 'edit' || action === 'delete') {
      payload.eventId = initialEvent?.id;
    }
    const result = await submitEvent(payload);
    setIsSubmitting(false);
    if (result.ok) {
      dispatch(
        showAlert({
          message: result.message || 'Event saved successfully',
          code: 'success'
        })
      );
      closeModal();
    } else {
      dispatch(
        showAlert({
          message: result.message || 'Something went wrong',
          code: 'error'
        })
      );
      setError(result.message || 'Something went wrong');
    }
  };

  const handleDelete = () => {
    if (!isEdit) return;
    handleSubmit('delete');
  };

  const footer = (
    <div className='flex gap-2 w-full'>
      <Button text='Cancel' variant='secondary' fill onClick={closeModal} />
      {isEdit && (
        <Button
          text='Delete'
          variant='danger'
          fill
          onClick={handleDelete}
          isDisabled={isSubmitting}
        />
      )}
      <Button
        text='Save'
        variant='primary'
        fill
        onClick={() => handleSubmit(isEdit ? 'edit' : 'add')}
        isDisabled={isSubmitting || (isEdit ? !canSaveEdit : !canSaveAdd)}
        isLoading={isSubmitting}
      />
    </div>
  );

  return (
    <Modal
      active
      closeModal={closeModal}
      title={isEdit ? 'Edit Calendar Event' : 'New Calendar Event'}
      footer={footer}
      isLoading={false}
    >
      <SimpleContainer className='gap-4 p-1'>
        {error && (
          <p className='text-sm text-red-600 rounded-md bg-red-50 px-3 py-2'>
            {error}
          </p>
        )}
        <div>
          <div className='flex items-center justify-between gap-2 mb-1'>
            <label
              htmlFor='new-event-title'
              className='block text-sm font-medium text-slate-700'
            >
              Title <span className='text-red-500'>*</span>
            </label>
            <span
              className={`text-xs shrink-0 ${
                title.length > TITLE_MAX
                  ? 'text-red-600 font-medium'
                  : 'text-slate-500'
              }`}
              aria-live='polite'
            >
              {Math.max(0, TITLE_MAX - title.length)} characters remaining
            </span>
          </div>
          <input
            id='new-event-title'
            type='text'
            maxLength={TITLE_MAX + 1}
            value={title}
            onChange={e => setTitle(e.target.value)}
            className='w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
            placeholder='Event title'
          />
          {titleError && (
            <p className='text-xs text-red-600 mt-0.5'>
              Title must be at most {TITLE_MAX} characters
            </p>
          )}
        </div>
        <div>
          <div className='flex items-center justify-between gap-2 mb-1'>
            <label
              htmlFor='new-event-description'
              className='block text-sm font-medium text-slate-700'
            >
              Description (optional)
            </label>
            <span
              className={`text-xs shrink-0 ${
                description.length > DESCRIPTION_MAX
                  ? 'text-red-600 font-medium'
                  : 'text-slate-500'
              }`}
              aria-live='polite'
            >
              {Math.max(0, DESCRIPTION_MAX - description.length)} characters
              remaining
            </span>
          </div>
          <input
            id='new-event-description'
            type='text'
            maxLength={DESCRIPTION_MAX + 1}
            value={description}
            onChange={e => setDescription(e.target.value)}
            className='w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
            placeholder='Optional description'
          />
          {descriptionError && (
            <p className='text-xs text-red-600 mt-0.5'>
              Description must be at most {DESCRIPTION_MAX} characters
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor='new-event-start'
            className='block text-sm font-medium text-slate-700 mb-1'
          >
            Start time <span className='text-red-500'>*</span>
          </label>
          <input
            id='new-event-start'
            type='datetime-local'
            value={startTimeLocal}
            onChange={e => setStartTimeLocal(e.target.value)}
            className='w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          />
        </div>
      </SimpleContainer>
    </Modal>
  );
}

NewEventModal.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  event: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string
  }),
  closeModal: PropTypes.func.isRequired,
  submitEvent: PropTypes.func.isRequired
};
